/* Pure decision logic for the guided tour room.

   Split out of the components for the same reason leadGateFlow.ts was:
   this feature is a permission model plus a state machine plus a set of
   defensive fallbacks, and none of that is worth testing through a
   browser. Everything here is a pure function — no React, no network, no
   storage — so the rules can be asserted directly.

   The components own rendering and effects; this file owns the answers.
*/

/* ── Admission (the C1/C2/C3 join model) ──────────────────────────────
   The BACKEND decides. This page is public and has no access to the
   agency's lead list, so any client-side version of "is this a known
   lead" would be forged by editing a response in devtools.

     C1  mobile is the primary lead the room was created for
           → ADMITTED, straight in, no host approval
     C2  mobile is another lead already on the agency's book
           → WAITING, host approves
     C3  mobile is unknown
           → WAITING + needs_profile; lead created, source = guided_tour

   Only C1 skips the lobby. Absent any decision we fall through to
   WAITING — the safe direction to be wrong in. */
export interface Admission {
  admitted: boolean;
  needsProfile: boolean;
}

export const readAdmission = (res: any): Admission => {
  const status = String(res?.lobby_status || res?.status || "").toUpperCase();
  return {
    admitted: status === "ADMITTED" || res?.auto_admitted === true,
    needsProfile: res?.needs_profile === true || res?.is_new_lead === true,
  };
};

/* ── Lobby ────────────────────────────────────────────────────────────
   The lobby response shape isn't pinned down in either spec doc, and the
   endpoint predates the host UI. Rather than bet on one shape, accept the
   three plausible ones and partition on lobby_status ourselves — a wrong
   guess renders an empty lobby, which is the worst possible failure for
   this screen (buyers wait forever and the host sees nobody). */
export type LobbyStatus = "WAITING" | "ADMITTED" | "LEFT" | "REJECTED" | string;

export interface Participant {
  session_id: string;
  display_name: string;
  role: string;
  lobby_status: LobbyStatus;
  muted?: boolean;
  invited_by_name?: string;
  joined_at?: string;
}

export const normalizeLobby = (res: any): Participant[] => {
  const waitingBucket: any[] = Array.isArray(res?.waiting) ? res.waiting : [];
  const rows: any[] = Array.isArray(res)
    ? res
    : res?.participants || res?.lobby ||
      [...waitingBucket, ...(Array.isArray(res?.admitted) ? res.admitted : [])];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r: any) => ({
      session_id: r?.session_id || r?.id || "",
      display_name: r?.display_name || r?.first_name || r?.name || "Guest",
      role: String(r?.role || "lead").toLowerCase(),
      /* A row that arrived under `waiting`/`admitted` may carry no status
         of its own — infer it from the bucket it came in. */
      lobby_status: String(
        r?.lobby_status || r?.status ||
        (waitingBucket.includes(r) ? "WAITING" : "ADMITTED"),
      ).toUpperCase(),
      muted: !!r?.muted,
      invited_by_name: r?.invited_by_name || r?.invited_by || "",
      joined_at: r?.joined_at,
    }))
    .filter((p: Participant) => p.session_id);
};

export const partitionLobby = (people: Participant[]) => ({
  waiting: people.filter((p) => p.lobby_status === "WAITING"),
  inRoom: people.filter((p) => p.lobby_status === "ADMITTED"),
});

/* ── Room lifecycle ───────────────────────────────────────────────────
   States in which a room still owns its project. Anything else (ENDED,
   CANCELLED, EXPIRED) frees it for a new one. */
export const LIVE_STATES = ["SCHEDULED", "LOBBY_OPEN", "LIVE"];

export const isRoomLive = (room: any): boolean =>
  LIVE_STATES.includes(String(room?.status || room?.state || "").toUpperCase());

/* Is this HTTP status "the room is gone" rather than "not your turn yet"?
   The distinction matters on the buyer's waiting screen: a participant
   who isn't admitted yet gets 403, and treating every error as "keep
   waiting" strands anyone who opens a link for a finished tour on a
   spinner forever — with a cached credential putting them straight back
   there on refresh. 404/410 mean stop and say so. */
export const isRoomGone = (httpStatus?: number): boolean =>
  httpStatus === 404 || httpStatus === 410;

/* ── WHAT THE BACKEND ACTUALLY SAID ───────────────────────────────────
   This API answers errors in three different shapes, and reading only one
   of them throws away the good ones.

     { detail: { message: "Room is at capacity (8). Remove someone…" } }   409, guided room
     { detail: "This guided tour has ended." }                             FastAPI default
     { error_message: "Cannot save an already existing obj…" }             the ORM layer

   The host clicked Admit on a full room and saw nothing, because the
   extractor looked for `error_message`, found nothing, and fell through
   to axios's own "Request failed with status code 409" — a sentence about
   HTTP, offered to somebody running a property tour. Meanwhile the
   backend had written exactly the right words and even said which action
   would fix it. Found 2026-09-10.

   Prefers the specific over the generic, and never returns an object:
   `detail` is sometimes a dict, and rendering one puts "[object Object]"
   on screen — which is how this kind of bug usually announces itself. */
export const apiMessage = (e: any, fallback = "That didn't work."): string => {
  const data = e?.response?.data;
  const detail = data?.detail;

  const candidates = [
    typeof detail === "string" ? detail : "",
    typeof detail?.message === "string" ? detail.message : "",
    typeof detail?.detail === "string" ? detail.detail : "",
    typeof data?.error_message === "string" ? data.error_message : "",
    typeof data?.message === "string" ? data.message : "",
  ];

  const found = candidates.map((s) => String(s || "").trim()).find(Boolean);
  if (found) return found;

  /* FastAPI's 422 answers with an ARRAY of field errors, which every
     caller here was rendering as "[object Object]" — or, in React, not
     rendering at all. Name the fields instead: "email id is required"
     tells the person what to do, which is the whole job of this
     function. */
  if (Array.isArray(detail) && detail.length) {
    const parts = detail
      .map((d: any) => {
        const field = String(
          (Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : "") || "",
        ).replace(/_/g, " ").trim();
        const msg = String(d?.msg || "").trim();
        if (!field && !msg) return "";
        if (!field) return msg;
        return msg.toLowerCase().includes("required")
          ? `${field} is required`
          : `${field}: ${msg}`;
      })
      .filter(Boolean);
    if (parts.length) {
      const s = parts.join("; ");
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
  }

  /* axios's own message names a status code and nothing a person can act
     on, so it is worse than our fallback rather than better. */
  return fallback;
};

/* ── WHY A FAMILY MEMBER WAS TURNED AWAY ──────────────────────────────
   A guest join can fail three ways, and each deserves a different screen:

     full     the family link has used all its places — an ordinary
              outcome, nobody did anything wrong
     gone     the tour has ended or the room no longer exists
     invalid  anything else — an expired or unrecognised link

   Pulled out of the join page on 2026-09-11 because this exact decision
   broke twice. First the backend's `detail` turned from a string into an
   object and the page did String(detail) → "[object Object]", matching
   nothing. Then the refusal answered 403 where the page only accepted
   409. Both times a fifth relative was shown a red "That invite link
   isn't valid any more" under a form field, instead of the calm screen
   written for exactly their situation. Inline in a component it could not
   be tested; here it can.

   "invalid" is the fallback on purpose, but it is no longer an inline
   error — see the join page, which gives it a full screen of its own. A
   rejection should still feel like the product, not like a form that
   broke. */
export type GuestRefusal = "full" | "gone" | "invalid";

export const classifyGuestRefusal = (e: any): GuestRefusal => {
  const status = e?.response?.status;
  if (isRoomGone(status)) return "gone";
  const d = e?.response?.data;
  const said = apiMessage(e, "");
  if (
    status === 409
    || d?.exhausted === true
    || d?.detail?.exhausted === true
    || /exhaust|limit|full|no (seats|spots)|quota|all .* places/i.test(said)
  ) return "full";
  if (/ended|no longer|finished/i.test(said)) return "gone";
  return "invalid";
};

/* The room's display fields, when a refusal carries them. A pass holder
   has no join code and therefore no preview, so a refusal may be the only
   description of the room they are ever given — and the screen that turns
   them away should still name the home they came to see.

   `invited_by_name` is read now, before the backend sends it, so the
   moment it does the page says "Arjun invited you" with no release. */
export const refusalPreview = (e: any): Record<string, any> | null => {
  const detail = e?.response?.data?.detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;
  const shown: Record<string, any> = {
    project_id: detail.project_id,
    project_name: detail.project_name,
    agency_name: detail.agency_name,
    agency_logo_url: detail.agency_logo_url,
    host_first_name: detail.host_first_name,
    recording_enabled: detail.recording_enabled,
    invited_by_name: detail.invited_by_name,
  };
  Object.keys(shown).forEach((k) => { if (shown[k] === undefined || shown[k] === null) delete shown[k]; });
  return Object.keys(shown).length ? shown : null;
};

/* How many places the link had. Prefers a structured field; otherwise
   reads it out of the message ("Guest limit (4) reached for this invite"),
   which is where the figure currently lives. Null rather than a guess —
   the caller keeps its own default. */
export const refusalCap = (e: any): number | null => {
  const d = e?.response?.data;
  const structured = Number(d?.max ?? d?.limit ?? d?.max_uses ?? d?.detail?.max ?? d?.detail?.limit);
  if (Number.isFinite(structured) && structured > 0) return structured;
  const fromText = Number((apiMessage(e, "").match(/\((\d+)\)/) || [])[1]);
  return Number.isFinite(fromText) && fromText > 0 ? fromText : null;
};

/* ── Viewer role ──────────────────────────────────────────────────────
   host  — created the room; runs the lobby, can end it
   staff — a colleague on the invite list; enters directly, drives nothing

   The backend is the authority (it knows the host and the invite list and
   derives from the token). Until it returns `viewer_role`, fall back to
   "did this browser create the room" — the host credential is only ever
   written on create. That degrades a colleague to view-only, which is the
   safe direction. */
export type ViewerRole = "host" | "staff";

export const resolveViewerRole = (room: any, hasHostCred: boolean): ViewerRole => {
  const declared = String(room?.viewer_role || "").toLowerCase();
  if (declared === "host" || declared === "staff") return declared as ViewerRole;
  return hasHostCred ? "host" : "staff";
};

/* ── WhatsApp ─────────────────────────────────────────────────────────
   WhatsApp wants a bare international number: no +, no spaces, no dashes.
   Assumes +91 when the stored number is a local 10-digit one, and strips
   the trunk 0 from an 11-digit local format. */
export const waNumber = (mobile?: string): string => {
  const d = String(mobile || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 10) return `91${d}`;
  if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`;
  return d;
};

/* api.whatsapp.com/send, NEVER wa.me — wa.me's redirect step mangles
   4-byte/astral emoji into the replacement character while BMP characters
   survive, so a message looks right in a preview and arrives broken. */
export const waLink = (mobile: string | undefined, text: string): string => {
  const n = waNumber(mobile);
  const t = encodeURIComponent(text);
  return n
    ? `https://api.whatsapp.com/send?phone=${n}&text=${t}`
    : `https://api.whatsapp.com/send?text=${t}`;
};

/* Emoji are assembled at RUNTIME from code points. Literal emoji and \u
   escapes both render as the replacement character — the build pipeline
   mangles non-ASCII in the bundle. Keeps source and bundle pure ASCII. */
export const cp = (...c: number[]) => String.fromCodePoint(...c);

export const firstNameOf = (full: string) =>
  String(full || "").trim().split(/\s+/)[0] || "";

/* ── Who a host may invite ────────────────────────────────────────────
   An agent must not be able to open a room for somebody else's lead, and
   only an ABA sees system admins. Agents get no tab strip at all because
   leads are the only thing they can add. */
export type PersonKind = "lead" | "agent" | "asa";

export interface ViewerRoles {
  isABA?: boolean;
  isASA?: boolean;
}

export const directoryKindsFor = (r: ViewerRoles): PersonKind[] => {
  if (r.isABA) return ["lead", "agent", "asa"];
  if (r.isASA) return ["lead", "agent"];
  return ["lead"];
};

/* ── Capacity ─────────────────────────────────────────────────────────
   CAPTURING a lead and OCCUPYING A SEAT are different things. There is
   deliberately NO cap on lead conversion — a forwarded link is a
   distribution channel, and everyone who verifies becomes an attributed
   lead whether or not they enter the room. The cap here is concurrent
   seats: Agora's interactive tier, and one host working a lobby. */
export const MAX_LEADS = 2;
export const MAX_STAFF = 2;
/* A STARTING NUMBER, not a considered limit — founder expects to raise it
   to 6 or 8 once there's real adoption to look at, so it's asked of the
   backend as configuration rather than a constant (see §A.7 of
   GUIDED_ROOM_JOIN_OTP_ALIGNMENT.md). Use this only for capacity COPY
   before a pass exists; anywhere the server has told us a `max`, that
   value wins — otherwise raising the cap silently disagrees with the
   count the guest is being shown.

   One bucket per lead, and buckets never pool: two invited leads means
   two independent tokens of four, so L1's family exhausting their link
   leaves L2's untouched. */
export const MAX_GUEST_PASSES_PER_LEAD = 4;

export interface Capacity {
  maxLeads: number;
  maxStaff: number;
  /* INVITED seats only — host + buyers + colleagues + their family. This
     is NOT the room's ceiling and must not be shown to a host as one: a
     forwarded link converts an uncapped number of new leads, any of whom
     the host can admit. The UI used to quote it as "around 13 in the room
     at once", which was wrong. */
  roomTotal: number;
}

export const capacityFor = (canInviteStaff: boolean): Capacity => {
  const maxStaff = canInviteStaff ? MAX_STAFF : 0;
  return {
    maxLeads: MAX_LEADS,
    maxStaff,
    roomTotal:
      1 +                                    // host
      MAX_LEADS +                            // buyers invited at creation
      maxStaff +                             // colleagues sitting in
      MAX_LEADS * MAX_GUEST_PASSES_PER_LEAD, // their family
  };
};

/* Caps are per-kind, not one shared headcount — a room is a household
   tour, so two buyers plus two colleagues is the shape. */
export const canAddPerson = (
  kind: PersonKind, leadCount: number, staffCount: number, cap: Capacity,
): boolean => (kind === "lead"
  ? leadCount < cap.maxLeads
  : staffCount < cap.maxStaff);

/* ── Invite links ─────────────────────────────────────────────────────
   Buyers and colleagues get DIFFERENT doors, and sending a colleague the
   buyer link is the expensive mistake: they'd OTP in, hit case C3 and be
   filed as a sales lead — our own staff member in the CRM. */
export interface LinkParts {
  origin: string;
  joinCode: string;
  roomId: string;
}

export const inviteLinkFor = (kind: PersonKind, p: LinkParts): string => {
  if (kind === "lead") return `${p.origin}/visit/${p.joinCode}`;
  /* Staff sign in with email + password and land in the room directly.
     Falls back to the buyer link only if the room id is somehow absent —
     better a working link than a broken one. */
  return p.roomId ? `${p.origin}/room/${p.roomId}` : `${p.origin}/visit/${p.joinCode}`;
};

/* ── One live room per HOST ───────────────────────────────────────────
   Keyed by PROJECT originally, which is the wrong axis. The backend caps
   one live room per AGENT and one live tour per BUYER, so:

     - two agents touring the SAME project for different buyers is legal,
       and a project-keyed claim wrongly blocked the second one;
     - one agent opening a SECOND room on a different project is not, and
       a project-keyed claim let it through to a 409.

   The claim lives in localStorage rather than memory because the room
   runs in a separate tab: `storage` events are what tell the project page
   that a tour ended somewhere else.

   v2 because a v1 entry is keyed by project id and would be read here as
   a host id. The two never collide in practice, but the old entries would
   linger forever — the host page sweeps them on mount. */
export const ACTIVE_KEY = "guidedRoom:active:v2";
export const LEGACY_ACTIVE_KEY = "guidedRoom:active:v1";
export const HOST_CRED_KEY = "guidedRoom:host:v1";

export const activeKeyFor = (hostId: string) => `${ACTIVE_KEY}:${hostId}`;
export const hostCredKeyFor = (roomId: string) => `${HOST_CRED_KEY}:${roomId}`;

/* Does this storage event concern us? A null key means "storage was
   cleared", which must also trigger a re-check. */
export const affectsActiveRoom = (eventKey: string | null, hostId: string) =>
  eventKey === null || eventKey === activeKeyFor(hostId);

/* ── Local preview bridge (development only) ──────────────────────────
   The join page is public and holds only a join code. Turning that into
   a project needs GET /views/guided_room/join/{join_code}, which is not
   on the backend yet — so on a developer's machine the page has nothing
   real to show and used to fall back to the design's sample copy, which
   confidently described a project you weren't looking at.

   The host's browser DOES know: it just created the room. Writing the
   project here at creation lets the join page read the REAL project in
   the same browser, instead of inventing one.

   Strictly a dev aid — it only works because both tabs are the same
   browser, which is never true of an actual buyer. The join page gates
   reading it on localhost. Delete both sides when the endpoint ships. */
export const LOCAL_PREVIEW_KEY = "guidedRoom:localPreview:v1";

export const localPreviewKeyFor = (joinCode: string) =>
  `${LOCAL_PREVIEW_KEY}:${joinCode}`;

/* The claim carries its project because it is now read from OTHER
   projects' pages, which need to name the tour they're being blocked by. */
export interface ActiveClaim {
  room_id: string;
  join_code: string;
  project_id?: string;
  project_name?: string;
}

/* What the Guided Tour button should say on THIS project's page.

   live_here      — the host's live room is this project's: "Tour live", rejoin
   live_elsewhere — the host is running a tour on another project, so creating
                    here would 409: say so, and offer the room they actually have
   free           — no known claim; create away

   An absent hostId means we could not identify the viewer, and guessing
   would block a legitimate host. Fall through to `free` and let the
   backend's 409 be the real gate — it is the authority either way. */
export type HostAvailability =
  | { state: "free" }
  | { state: "live_here"; claim: ActiveClaim }
  | { state: "live_elsewhere"; claim: ActiveClaim };

export const hostAvailability = (
  claim: ActiveClaim | null | undefined,
  projectId: string,
  hostId: string,
): HostAvailability => {
  if (!hostId || !claim?.room_id) return { state: "free" };
  /* A claim written before project_id was stored can only have come from
     the page that wrote it, so treat it as this project's rather than
     stranding the host with an un-rejoinable room. */
  if (!claim.project_id || claim.project_id === projectId) {
    return { state: "live_here", claim };
  }
  return { state: "live_elsewhere", claim };
};

/* ── Room-conflict (409) ──────────────────────────────────────────────
   Two different rules land on the same status code and they need
   different words: the host can end their own room, but a buyer who is
   already touring with somebody else is not the host's to release.

   The response body isn't pinned down, so match on whatever text comes
   back across the plausible field names. BUYER is tested first because
   the host case is normally caught client-side by our own claim before
   the request is ever made — a 409 that reaches here is more often the
   buyer. An unrecognised body still gets a usable message. */
export type ConflictKind = "host_busy" | "buyer_busy" | "unknown";

export const readConflict = (err: any): ConflictKind | null => {
  if (err?.response?.status !== 409) return null;
  const b = err?.response?.data;
  const text = String(
    b?.code || b?.reason || b?.detail || b?.message || b?.error || "",
  ).toLowerCase();
  if (/lead|buyer|customer|guest/.test(text)) return "buyer_busy";
  if (/host|agent|user|room/.test(text)) return "host_busy";
  return "unknown";
};

/* ── WHICH room is in the way ─────────────────────────────────────────
   The backend has always answered the per-host 409 with the clashing
   room's id, state and join code — "the clashing room is the caller's
   OWN, so we hand back its join details". The client ignored every one of
   them and printed a sentence that named nothing, which left the host
   told they were blocked by something they could not find.

   This matters more than it looks: the host credential we keep in
   localStorage is per ORIGIN and per browser. A tour started on stage is
   invisible to the same person on a tunnel URL, or on their phone. The
   409 is the only account of it that travels. */
export interface ConflictRoom {
  room_id: string;
  join_code: string;
  state: string;
}

export const readConflictRoom = (err: any): ConflictRoom | null => {
  const d = err?.response?.data?.detail;
  const roomId = String(d?.active_room_id || "").trim();
  if (!roomId) return null;
  return {
    room_id: roomId,
    join_code: String(d?.join_code || "").trim(),
    state: String(d?.state || "").toUpperCase(),
  };
};

export const conflictMessage = (kind: ConflictKind, buyerName?: string): string => {
  if (kind === "buyer_busy") {
    const who = firstNameOf(buyerName || "") || "That buyer";
    return `${who} is already on a live tour. A buyer can only be on one at a time.`;
  }
  if (kind === "host_busy") {
    return "You already have a live tour running. End it before starting another.";
  }
  return "This clashes with a tour that's already live. End that one, then try again.";
};
